import { useState } from "react";
import { BadgeCheck, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCloseOnScroll } from "@/hooks/useCloseOnScroll";

type Size = "sm" | "md";

interface VerificationBadgeProps {
  verified: boolean;
  size?: Size;
}

/**
 * Badge weryfikacji ze "stuknij/kliknij, aby dowiedzieć się więcej".
 * Używa Popover (klik) zamiast Tooltip - działa zarówno na mobile (tap)
 * jak i na desktop. Zamyka się przy scrollu.
 */
export const VerificationBadge = ({ verified, size = "sm" }: VerificationBadgeProps) => {
  const [open, setOpen] = useState(false);
  useCloseOnScroll(open, () => setOpen(false));

  const sizeClasses =
    size === "md"
      ? "text-[11px] px-2 py-0.5 gap-1"
      : "text-[9px] sm:text-[10px] p-0.5 sm:px-1.5 sm:py-[1px] sm:leading-[14px] gap-0.5 sm:gap-1";
  const iconSize = size === "md" ? "w-3 h-3" : "w-3 h-3 sm:w-2.5 sm:h-2.5";

  const baseClasses = `inline-flex items-center font-semibold rounded-full border cursor-pointer transition-all shadow-sm ${sizeClasses}`;

  const labelClass = size === "md" ? "" : "hidden sm:inline";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={verified ? "Zweryfikowane - co to znaczy?" : "Niezweryfikowane - co to znaczy?"}
          className={
            verified
              ? `${baseClasses} bg-gradient-to-b from-primary/15 to-primary/5 text-primary border-primary/25 hover:from-primary/20 hover:to-primary/10 hover:border-primary/35`
              : `${baseClasses} bg-gradient-to-b from-muted to-muted/60 text-muted-foreground border-border hover:from-muted/90`
          }
        >
          {verified ? (
            <>
              <BadgeCheck className={iconSize} />
              <span className={labelClass}>
                {size === "md" ? "Zweryfikowane przez organizację" : "Zweryfikowane"}
              </span>
            </>
          ) : (
            <>
              <HelpCircle className={iconSize} />
              <span className={labelClass}>Niezweryfikowane</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        sticky="always"
        avoidCollisions
        className="w-[240px] text-xs leading-relaxed p-3 z-[2200]"
        onClick={(e) => e.stopPropagation()}
      >
        {verified ? (
          <>
            <p className="font-semibold text-foreground mb-1">Dane zweryfikowane przez organizację</p>
            <p className="text-muted-foreground">
              Informacje o schronisku zostały potwierdzone przez organizację lub jej wolontariuszy.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold text-foreground mb-1">Profil niezweryfikowany</p>
            <p className="text-muted-foreground">
              Dane z internetu, jeszcze niepotwierdzone. Masz aktualne info? Otwórz profil i zgłoś aktualizację.
            </p>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
